import { useCallback, useMemo } from 'react'

import { PrivateMessagingOutgoingContact } from '@tribeplatform/gql-client/types'
import { Form } from '@tribeplatform/react-components/Form'
import { T, useI18n } from '@tribeplatform/react-components/i18n'
import { useNetwork, useUpdateNetwork } from '@tribeplatform/react-sdk/hooks'
import { Button } from '@tribeplatform/react-ui-kit/Button'
import { Card } from '@tribeplatform/react-ui-kit/Card'
import { RadioGroup } from '@tribeplatform/react-ui-kit/RadioGroup'
import { toast } from '@tribeplatform/react-ui-kit/Toast'

export const MembersEnableMessaging = () => {
  const { $t } = useI18n()
  const { data: network } = useNetwork()
  const { mutateAsync: updateNetwork, isLoading } = useUpdateNetwork()

  const { privateMessagingEnabled, whoCanMembersContact } =
    network?.settings?.privateMessaging || {}

  const onSubmit = useCallback(
    async (data: { whoCanMembersContact: PrivateMessagingOutgoingContact }) => {
      try {
        await updateNetwork({
          input: {
            settings: {
              privateMessaging: {
                ...network?.settings?.privateMessaging,
                ...data,
              },
            },
          },
        })

        toast({
          title: $t({
            defaultMessage: 'Messaging settings updated.',
            id: 'Admin.Messaging.Settings.Updated',
          }),
          status: 'success',
        })
      } catch (error) {
        toast({
          title: $t({
            defaultMessage: 'Unable to update messaging settings.',
            id: 'Admin.Messaging.Settings.FailedToUpdate',
          }),
          status: 'error',
        })
      }
    },
    [$t, network?.settings?.privateMessaging, updateNetwork],
  )

  const defaultValues = useMemo(() => {
    return {
      whoCanMembersContact:
        whoCanMembersContact ?? PrivateMessagingOutgoingContact.Anyone,
    }
  }, [whoCanMembersContact])

  return (
    <Card>
      <Card.Header
        title={$t({
          defaultMessage: 'Sending and receiving messages',
          id: 'Admin.Messaging.SendAndReceive',
        })}
        description={$t({
          defaultMessage:
            'Choose whether {MEMBERS} can message each other or only communicate with admins and moderators.',
          id: 'Admin.Messaging.SendAndReceive.Description',
        })}
      />
      <Card.Content>
        <Form defaultValues={defaultValues} onSubmit={onSubmit}>
          {({ formState }) => (
            <Form.Layout>
              <Form.RadioGroup
                name="whoCanMembersContact"
                disabled={privateMessagingEnabled === false}
              >
                <RadioGroup.Items>
                  <RadioGroup.Item
                    value={PrivateMessagingOutgoingContact.Anyone}
                    title={$t({
                      defaultMessage: '{MEMBERS_CC} can message each other',
                      id: 'Admin.Messaging.SendAndReceive.All.Capital',
                    })}
                    description={$t({
                      defaultMessage:
                        'All {MEMBERS} can send and receive messages from each other.',
                      id: 'Admin.Messaging.SendAndReceive.All.Description',
                    })}
                  />
                  <RadioGroup.Item
                    value={PrivateMessagingOutgoingContact.Staff}
                    title={$t({
                      defaultMessage:
                        '{MEMBERS_CC} can only message admins and moderators',
                      id: 'Admin.Messaging.SendAndReceive.Limited.Capital',
                    })}
                    description={
                      <p>
                        {$t({
                          defaultMessage:
                            'Messaging between {MEMBERS} is disabled.',
                          id: 'Admin.Messaging.SendAndReceive.Limited.Rule.Member',
                        })}
                        <br />
                        {$t({
                          defaultMessage:
                            '{MEMBERS_CC} and admins/moderators can message each other.',
                          id: 'Admin.Messaging.SendAndReceive.Limited.Rule.Cross',
                        })}
                        <br />
                        {$t({
                          defaultMessage:
                            'Admins and moderators can message each other.',
                          id: 'Admin.Messaging.SendAndReceive.Limited.Rule.Admin',
                        })}
                      </p>
                    }
                  />
                </RadioGroup.Items>
              </Form.RadioGroup>
              <Form.Actions>
                <Button
                  type="submit"
                  loading={isLoading}
                  disabled={
                    privateMessagingEnabled === false || !formState.isDirty
                  }
                >
                  <T defaultMessage="Update" id="Generics.Update" />
                </Button>
              </Form.Actions>
            </Form.Layout>
          )}
        </Form>
      </Card.Content>
    </Card>
  )
}
